/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import ndarray from '@stdlib/ndarray-base-ctor';
import assign from '@stdlib/ndarray-base-assign';
import buffer from '@stdlib/ndarray-base-buffer';
import numel from '@stdlib/ndarray-base-numel';
import shape2strides from '@stdlib/ndarray-base-shape2strides';
import strides2order from '@stdlib/ndarray-base-strides2order';
import getShape from '@stdlib/ndarray-base-shape';
import getStrides from '@stdlib/ndarray-base-strides';
import getOffset from '@stdlib/ndarray-base-offset';
import getOrder from '@stdlib/ndarray-base-order';
import getDType from '@stdlib/ndarray-base-dtype';
import getData from '@stdlib/ndarray-base-data-buffer';
import zeros from '@stdlib/array-base-zeros';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns an ndarray created by repeating the elements of an input ndarray a specified number of times along each dimension.
*
* ## Notes
*
* -   The number of repetitions must have at least as many elements as the number of input dimensions. When the number of repetitions exceeds the number of input dimensions, the input array is treated as if singleton dimensions were prepended.
* -   The function always copies data to a new ndarray.
*
* @param {ndarray} x - input array
* @param {NonNegativeIntegerArray} reps - number of repetitions along each dimension
* @throws {RangeError} second argument must have at least as many elements as the number of input dimensions
* @returns {ndarray} output array
*
* @example
* import array from '@stdlib/ndarray-array';
*
* var x = array( [ [ 1, 2 ], [ 3, 4 ] ] );
* // returns <ndarray>[ [ 1, 2 ], [ 3, 4 ] ]
*
* var y = tile( x, [ 2, 2 ] );
* // returns <ndarray>[ [ 1, 2, 1, 2 ], [ 3, 4, 3, 4 ], [ 1, 2, 1, 2 ], [ 3, 4, 3, 4 ] ]
*/
function tile( x, reps ) {
	var dtype;
	var order;
	var shx;
	var sho;
	var shi;
	var sto;
	var buf;
	var len;
	var out;
	var Ds;
	var si;
	var sx;
	var v1;
	var v2;
	var M;
	var N;
	var o;
	var s;
	var t;
	var i;
	var j;
	var k;

	shx = getShape( x, false );
	sx = getStrides( x, false );
	dtype = getDType( x );
	N = reps.length;
	M = shx.length;

	if ( N < M ) {
		throw new RangeError( format( 'invalid argument. Second argument must have at least as many elements as the number of dimensions of the first argument. Number of input dimensions: `%u`. Number of repetitions: `%u`.', M, N ) );
	}
	// Compute the implicit left-padding amount for the input shape:
	Ds = N - M;

	o = strides2order( sx );
	if ( o === 0 || o === 3 ) {
		// Fallback to stated layout when unable to infer the underlying physical layout:
		order = getOrder( x );
	} else if ( o === 1 ) {
		order = 'row-major';
	} else {
		order = 'column-major';
	}
	// Compute an interleaved 2N-D shape and strides for "broadcasting" the input, along with the final N-D output shape. By inserting a repetition axis next to each original axis, a contiguous 2N-D buffer can be reinterpreted as the N-D tiled output without an additional copy...
	shi = zeros( 2*N );
	si = zeros( 2*N );
	sho = zeros( N );
	for ( i = 0; i < N; i++ ) {
		j = i - Ds;
		k = 2 * i;
		s = ( j < 0 ) ? 1 : shx[ j ];
		t = ( j < 0 ) ? 0 : sx[ j ];
		sho[ i ] = reps[ i ] * s;
		if ( order === 'row-major' ) {
			shi[ k ] = reps[ i ];
			shi[ k+1 ] = s;
			si[ k+1 ] = t;
		} else { // order === 'column-major'
			shi[ k ] = s;
			shi[ k+1 ] = reps[ i ];
			si[ k ] = t;
		}
	}
	if ( N > 0 ) {
		len = numel( sho );
		sto = shape2strides( sho, order );
	} else {
		len = 1;
		sto = [ 0 ];
	}
	// Allocate an output array:
	buf = buffer( dtype, len );
	out = new x.constructor( dtype, buf, sho, sto, 0, order );

	// Create a "broadcasted" input 2N-D view over the data buffers:
	v1 = ndarray( dtype, getData( x ), shi, si, getOffset( x ), order );
	v2 = ndarray( dtype, buf, shi, shape2strides( shi, order ), 0, order );

	// Copy the elements to the output array:
	assign( [ v1, v2 ] );

	return out;
}


// EXPORTS //

export default tile;
